import * as React from "react"
import { X } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { LoadingSpinner } from "@/components/shared/loading-spinner"
import { CharacterCounter } from "@/components/shared/character-counter"
import {
  getAlfredMaxLengthError,
  MAX_ALFRED_INDEXED_STRING_LENGTH,
} from "@/lib/alfred-field-validation"
import type { CreateAlfredClientRequest } from "@/types/alfred-client"

interface AddAlfredClientFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CreateAlfredClientRequest) => Promise<void>
  isSubmitting?: boolean
}

interface FormData {
  clientId: string
  venueCode: string
}

interface FormErrors {
  clientId?: string
  venueCode?: string
}

const initialFormData: FormData = { clientId: "", venueCode: "" }

export function AddAlfredClientForm({ open, onOpenChange, onSubmit, isSubmitting = false }: AddAlfredClientFormProps) {
  const [formData, setFormData] = React.useState<FormData>(initialFormData)
  const [errors, setErrors] = React.useState<FormErrors>({})
  const [touched, setTouched] = React.useState<Record<string, boolean>>({})
  const firstInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (open) {
      setFormData(initialFormData)
      setErrors({})
      setTouched({})
      setTimeout(() => firstInputRef.current?.focus(), 100)
    }
  }, [open])

  const validateField = (name: keyof FormData, value: string): string | undefined => {
    if (!value.trim()) return `${name === "clientId" ? "Client ID" : "Venue Code"} is required`
    if (name === "clientId") {
      const lengthError = getAlfredMaxLengthError("Client ID", value)
      if (lengthError) return lengthError
    }
    if (name === "clientId" && !/^[A-Za-z0-9-]+$/.test(value)) {
      return "Client ID can only contain letters, numbers, and hyphens"
    }
    return undefined
  }

  const handleBlur = (name: keyof FormData) => {
    setTouched((prev) => ({ ...prev, [name]: true }))
    setErrors((prev) => ({ ...prev, [name]: validateField(name, formData[name]) }))
  }

  const handleChange = (name: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      clientId: validateField("clientId", formData.clientId),
      venueCode: validateField("venueCode", formData.venueCode),
    }
    setErrors(newErrors)
    setTouched({ clientId: true, venueCode: true })
    return !newErrors.clientId && !newErrors.venueCode
  }

  const canSubmit = !errors.clientId && !errors.venueCode && formData.clientId !== "" && formData.venueCode !== ""

  const handleSubmit = async () => {
    if (!validateForm()) return
    await onSubmit({ clientId: formData.clientId, venueCode: formData.venueCode })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[540px] sm:max-w-[540px] bg-card border-l border-border p-0 flex flex-col [&>button]:hidden"
      >
        <SheetHeader className="px-6 py-5 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="text-lg font-semibold text-foreground">Add New Client</SheetTitle>
              <SheetDescription className="text-muted-foreground mt-1">
                Configure a new Alfred client for the system
              </SheetDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="text-muted-foreground hover:text-foreground hover:bg-secondary -mr-2 -mt-1"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Client Identification
              </h3>
              <div className="flex-1 h-px bg-border" />
            </div>

            <div className="space-y-2">
              <Label className="text-[13px] text-muted-foreground font-medium">
                Client ID <span className="text-destructive">*</span>
              </Label>
              <Input
                ref={firstInputRef}
                value={formData.clientId}
                onChange={(e) => handleChange("clientId", e.target.value)}
                onBlur={() => handleBlur("clientId")}
                placeholder="e.g., dodgers"
                className={`font-mono bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                  errors.clientId && touched.clientId ? "border-destructive" : ""
                }`}
              />
              {errors.clientId && touched.clientId && (
                <p className="text-xs text-destructive">{errors.clientId}</p>
              )}
              <CharacterCounter
                value={formData.clientId}
                maxLength={MAX_ALFRED_INDEXED_STRING_LENGTH}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[13px] text-muted-foreground font-medium">
                Venue Code <span className="text-destructive">*</span>
              </Label>
              <Input
                value={formData.venueCode}
                onChange={(e) => handleChange("venueCode", e.target.value)}
                onBlur={() => handleBlur("venueCode")}
                placeholder="e.g., LAD"
                className={`font-mono bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                  errors.venueCode && touched.venueCode ? "border-destructive" : ""
                }`}
              />
              {errors.venueCode && touched.venueCode && (
                <p className="text-xs text-destructive">{errors.venueCode}</p>
              )}
            </div>
          </div>
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border flex-row justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !canSubmit}
            className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
          >
            {isSubmitting && <LoadingSpinner size="sm" className="mr-2" />}
            {isSubmitting ? "Creating..." : "Create Client"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
